/**
 * src/state/actions/useGovernanceActions.js
 *
 * Governance-domain action callbacks extracted from useAppState.js as
 * part of the file-splitting refactor. Each useCallback body is moved
 * verbatim — same statements, same dependency arrays, same closure
 * semantics — only the surrounding lexical scope changes (the closure
 * over `updateState`, `tenantId`, `session` is now via the sub-hook's
 * arguments rather than the parent hook's parameters).
 *
 * Sub-hook: useGovernanceActions({ updateState, tenantId, session })
 *
 * Returns (in canonical order):
 *   enableObligation, disableObligation,
 *   saveAdhocTask, deleteAdhocTask, saveControlTopic, deleteControlTopic,
 *   saveManagementReview, saveRopa, deleteRopa,
 *   saveThreatAnalysis
 */

import { useCallback } from 'react';
import { logActivity } from '../../api/activityLogger.js';
import { emitEngineEvent } from '../../engine/engineEventBus.js';
import { isValidTaskTransition } from '../../utils/taskTransitions.js';
import { enableObligationsNow } from './obligationEnable.js';

/**
 * Measurement ARCH §8.2 — stamp `closedAt` when an ad-hoc task's status
 * becomes 'closed', clear it when a closed task is reopened. A save that does
 * not change the status leaves the task exactly as posted, so legacy tasks
 * without `closedAt` stay untouched until they next change status.
 */
export function stampAdhocTaskClosure(prev, task, now = new Date().toISOString()) {
  const prevStatus = prev ? prev.status : undefined;
  if (task.status === prevStatus) return task;
  if (task.status === 'closed') return { ...task, closedAt: now };
  if (prevStatus === 'closed') return { ...task, closedAt: null };
  return task;
}

export function useGovernanceActions({ updateState, tenantId, session }) {
  // msr 4.1b (GH #7351): enabling is a server action (obligations-enable)
  // that stamps who and when and logs the activity; state-write no longer
  // takes a new enablement. Resolves once the server answered.
  const enableObligation = useCallback(
    (obligationId, options = {}) =>
      enableObligationsNow(updateState, [obligationId], {
        ...(options.assigneeId !== undefined ? { assigneeId: options.assigneeId || null } : {}),
      }),
    [updateState]
  );

  const disableObligation = useCallback(
    (obligationId) => {
      updateState((s) => {
        s.enabledObligations = (s.enabledObligations || []).filter(
          (o) => o.obligationId !== obligationId
        );
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Obligations',
          actionType: 'disabled',
          recordId: obligationId,
          recordLabel: obligationId,
        });
      }
    },
    [updateState, tenantId]
  );

  const saveAdhocTask = useCallback(
    (task, session) => {
      let isEdit = false;
      updateState((s) => {
        if (!s.adhocTasks) s.adhocTasks = [];
        const idx = s.adhocTasks.findIndex((t) => t.id === task.id);
        isEdit = idx >= 0;
        if (isEdit) {
          const prev = s.adhocTasks[idx];
          if (prev.status !== task.status && !isValidTaskTransition(prev.status, task.status)) {
            console.warn(`Blocked adhoc task transition: ${prev.status} → ${task.status}`);
            return;
          }
          s.adhocTasks[idx] = stampAdhocTaskClosure(prev, task);
        } else {
          s.adhocTasks = [stampAdhocTaskClosure(null, task), ...s.adhocTasks];
        }
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Tasks',
          actionType: isEdit ? 'edited' : 'created',
          recordId: task.id,
          recordLabel: task.title,
        });
      }
    },
    [updateState, tenantId]
  );

  const deleteAdhocTask = useCallback(
    (taskId, session) => {
      updateState((s) => {
        s.adhocTasks = (s.adhocTasks || []).filter((t) => t.id !== taskId);
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Tasks',
          actionType: 'deleted',
          recordId: taskId,
        });
      }
    },
    [updateState, tenantId]
  );

  const saveControlTopic = useCallback(
    (topic, session) => {
      let isEdit = false;
      updateState((s) => {
        if (!s.controlTopics) s.controlTopics = [];
        const idx = s.controlTopics.findIndex((t) => t.id === topic.id);
        isEdit = idx >= 0;
        if (isEdit) s.controlTopics[idx] = topic;
        else s.controlTopics = [...s.controlTopics, topic];
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Settings',
          actionType: isEdit ? 'edited' : 'created',
          recordId: topic.id,
          recordLabel: `Control topic: ${topic.name}`,
        });
      }
    },
    [updateState, tenantId]
  );

  const deleteControlTopic = useCallback(
    (topicId, session) => {
      let label = topicId;
      updateState((s) => {
        if (!s.controlTopics) return;
        const t = s.controlTopics.find((t) => t.id === topicId);
        if (t) label = t.name;
        s.controlTopics = s.controlTopics.filter((t) => t.id !== topicId);
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Settings',
          actionType: 'deleted',
          recordId: topicId,
          recordLabel: `Control topic: ${label}`,
        });
      }
    },
    [updateState, tenantId]
  );

  const saveManagementReview = useCallback(
    (review, session) => {
      let isEdit = false;
      updateState((s) => {
        if (!s.managementReviews) s.managementReviews = [];
        const idx = s.managementReviews.findIndex((r) => r.id === review.id);
        isEdit = idx >= 0;
        if (isEdit) s.managementReviews[idx] = review;
        else s.managementReviews = [review, ...s.managementReviews];
      });

      emitEngineEvent('managementReview.saved', { review });

      if (session) {
        logActivity(tenantId, session, {
          module: 'ManagementReview',
          actionType: isEdit ? 'edited' : 'created',
          recordId: review.id,
          recordLabel: review.title,
        });
      }
    },
    [updateState, tenantId]
  );

  // No deleteManagementReview: a review with recorded outputs is part of the
  // 9.3 record and is never deleted (MGMTREVIEW-B-002, D-1 A on GH #7167), and
  // state-write restores one if anything tries. No screen ever called it.

  const saveRopa = useCallback(
    (entry, session) => {
      let isEdit = false;
      updateState((s) => {
        if (!s.ropaEntries) s.ropaEntries = [];
        const idx = s.ropaEntries.findIndex((r) => r.id === entry.id);
        isEdit = idx >= 0;
        if (isEdit) s.ropaEntries[idx] = entry;
        else s.ropaEntries = [entry, ...s.ropaEntries];
      });

      if (session) {
        logActivity(tenantId, session, {
          module: 'ROPA',
          actionType: isEdit ? 'edited' : 'created',
          recordId: entry.id,
          recordLabel: entry.name,
        });
      }
    },
    [updateState, tenantId]
  );

  const deleteRopa = useCallback(
    (id, label, session) => {
      updateState((s) => {
        s.ropaEntries = (s.ropaEntries || []).filter((r) => r.id !== id);
      });

      if (session) {
        logActivity(tenantId, session, {
          module: 'ROPA',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }
    },
    [updateState, tenantId]
  );

  const saveThreatAnalysis = useCallback(
    (analysis) => {
      updateState((s) => {
        if (!s.threatAnalyses) s.threatAnalyses = [];
        const idx = s.threatAnalyses.findIndex((a) => a.id === analysis.id);
        if (idx >= 0) s.threatAnalyses[idx] = analysis;
        else s.threatAnalyses.push(analysis);
      });
    },
    [updateState]
  );

  /**
   * Complete a Tier 2 evidence-request action (ADR-0003).
   *
   * Mirrors completeRiskAction, minus the canonical risk write — evidence
   * requests live in the main state blob, not the risk store.
   */
  const completeEvidenceRequestAction = useCallback(
    (evidenceRequestId, actionId, completionData = {}) => {
      let title = '';
      updateState((s) => {
        const req = (s.evidenceRequests || []).find((r) => r.id === evidenceRequestId);
        if (!req || !req.actions) return;
        const action = req.actions.find((a) => a.id === actionId);
        // Completed is done; cancelled is settled with a reason and is not
        // work to complete (D-2 A on GH #7167).
        if (!action || action.status === 'completed' || action.status === 'cancelled') return;
        action.status = 'completed';
        action.completedAt = new Date().toISOString();
        action.completedBy = completionData.userId || '';
        if (completionData.evidenceRef) {
          action.evidenceRefs = [...(action.evidenceRefs || []), completionData.evidenceRef];
        }
        req.updatedAt = new Date().toISOString();
        title = action.title || '';
      });
      if (session && title) {
        logActivity(tenantId, session, {
          module: 'Findings',
          actionType: 'completed',
          recordId: actionId,
          recordName: title,
        });
      }
    },
    [updateState, tenantId, session]
  );

  return {
    completeEvidenceRequestAction,
    enableObligation,
    disableObligation,
    saveAdhocTask,
    deleteAdhocTask,
    saveControlTopic,
    deleteControlTopic,
    saveManagementReview,
    saveRopa,
    deleteRopa,
    saveThreatAnalysis,
  };
}
